// src/services/costs/controlCalculations.js
// Berechnungslogik fuer die Kostenkontrolle (SIA-Phase 41).
//
// Modell (bewusst analog zu aggregateTree in calculations.js):
//   - Eine AKTIVE Node ist eine genutzte Position (Blatt): sie verwendet ihre
//     eigenen Werte (KvFixed, Erwartung, Summe Vertraege, Summe Zahlungen).
//   - Eine INAKTIVE Node MIT aktiven Nachkommen ist eine Aggregat-Zeile: sie
//     summiert die Werte ihrer Kinder.
//   - Eine INAKTIVE Node OHNE aktive Nachkommen ist "ungenutzt" (KOST-011).
//
// Prognose pro Position = max(Vertrag + Erwartung, Summe Zahlungen).
// Prognose eines Aggregats = Summe der Kind-Prognosen (KOST-014: eine
// Ueberpositions-Zahlung veraendert die Aggregat-Prognose NICHT).

const num = (v) => (typeof v === "number" ? v : parseFloat(v) || 0);
const round2 = (v) => Math.round((num(v) + Number.EPSILON) * 100) / 100;

const sumBetrag = (list) => (list || []).reduce((s, x) => s + num(x.betrag), 0);

/**
 * Formatiert einen Betrag im CH-Format mit Apostroph-Tausendertrennung
 * (z.B. 1'234'560.00). Zwei Nachkommastellen.
 * @param {number} amount
 * @returns {string}
 */
export function formatSwissCurrency(amount) {
  return num(amount)
    .toLocaleString("de-CH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, "'");
}

/**
 * Berechnet fuer jede BKP-Position der Kostenkontrolle die Spaltenwerte.
 *
 * @param {Object.<string, object>} tree - BKP-Baum (Nodes mit kvFixed, erwartung, active, children)
 * @param {{ contractsByCode?: Object.<string, Array>, paymentsByCode?: Object.<string, Array> }} [data]
 * @returns {{ totals: Object.<string, object>, grand: object }}
 *   totals[code] = { kv, vertrag, erwartung, zahlung, prognose, diff,
 *                    isAggregate, isUnused, zahlungOverridden }
 */
export function computeControlTotals(tree, data = {}) {
  const contractsByCode = data.contractsByCode || {};
  const paymentsByCode = data.paymentsByCode || {};
  const totals = {};

  // Memoisierte Erkennung "hat aktive Nachkommen".
  const activeDescMemo = {};
  const hasActiveDescendant = (node) => {
    if (!node) return false;
    if (node.code in activeDescMemo) return activeDescMemo[node.code];
    // Zwischenspeicher setzen, um bei (theoretisch) zyklischen Referenzen
    // keine Endlosschleife zu riskieren.
    activeDescMemo[node.code] = false;
    let res = false;
    for (const c of node.children || []) {
      const child = tree[c];
      if (child && (child.active || hasActiveDescendant(child))) {
        res = true;
        break;
      }
    }
    activeDescMemo[node.code] = res;
    return res;
  };

  const compute = (node) => {
    if (!node) return null;
    if (totals[node.code]) return totals[node.code];

    const ownKv = round2(node.kvFixed);
    const ownErwartung = round2(node.erwartung);
    const ownVertrag = round2(sumBetrag(contractsByCode[node.code]));
    const ownZahlung = round2(sumBetrag(paymentsByCode[node.code]));

    let result;

    if (node.active) {
      // Genutzte Position (Blatt): eigene Werte.
      const prognose = round2(Math.max(ownVertrag + ownErwartung, ownZahlung));
      result = {
        kv: ownKv,
        vertrag: ownVertrag,
        erwartung: ownErwartung,
        zahlung: ownZahlung,
        prognose,
        diff: round2(prognose - ownKv),
        isAggregate: false,
        isUnused: false,
        zahlungOverridden: false,
      };
    } else if (hasActiveDescendant(node)) {
      // Aggregat-Zeile: Summe der Kinder.
      let kv = 0, vertrag = 0, erwartung = 0, childZahlung = 0, prognose = 0;
      for (const c of node.children || []) {
        const ct = compute(tree[c]);
        if (!ct) continue;
        kv += ct.kv;
        vertrag += ct.vertrag;
        erwartung += ct.erwartung;
        childZahlung += ct.zahlung;
        prognose += ct.prognose;
      }
      // KOST-014: Hat das Aggregat eigene (Ueberpositions-)Zahlungen, uebersteuert
      // deren Summe den Zahlungs-Rollup der Kinder. Die Prognose bleibt die
      // Summe der Kind-Prognosen (kleinste Ebene) und wird NICHT veraendert.
      const zahlungOverridden = ownZahlung > 0;
      result = {
        kv: round2(kv),
        vertrag: round2(vertrag),
        erwartung: round2(erwartung),
        zahlung: zahlungOverridden ? ownZahlung : round2(childZahlung),
        prognose: round2(prognose),
        diff: round2(prognose - kv),
        isAggregate: true,
        isUnused: false,
        zahlungOverridden,
      };
    } else {
      // Ungenutzte Position (inaktiv, keine aktiven Nachkommen).
      const prognose = round2(Math.max(ownVertrag + ownErwartung, ownZahlung));
      result = {
        kv: ownKv,
        vertrag: ownVertrag,
        erwartung: ownErwartung,
        zahlung: ownZahlung,
        prognose,
        diff: round2(prognose - ownKv),
        isAggregate: false,
        isUnused: true,
        zahlungOverridden: false,
      };
    }

    totals[node.code] = result;
    return result;
  };

  Object.values(tree).forEach((n) => compute(n));

  const grand = Object.values(tree)
    .filter((n) => n.level === 1)
    .reduce(
      (acc, n) => {
        const t = totals[n.code];
        if (!t) return acc;
        acc.kv += t.kv;
        acc.vertrag += t.vertrag;
        acc.erwartung += t.erwartung;
        acc.zahlung += t.zahlung;
        acc.prognose += t.prognose;
        return acc;
      },
      { kv: 0, vertrag: 0, erwartung: 0, zahlung: 0, prognose: 0 }
    );
  grand.kv = round2(grand.kv);
  grand.vertrag = round2(grand.vertrag);
  grand.erwartung = round2(grand.erwartung);
  grand.zahlung = round2(grand.zahlung);
  grand.prognose = round2(grand.prognose);
  grand.diff = round2(grand.prognose - grand.kv);

  return { totals, grand };
}

/**
 * %/H (KOST-008): Anteil des KV einer Position am KV-Total ihrer 1-stelligen
 * Hauptgruppe (Level-1-Vorfahre), eine Nachkommastelle. Reine Anzeige.
 *
 * @param {Object.<string, object>} tree
 * @param {Object.<string, object>} totals - aus computeControlTotals().totals
 * @returns {Object.<string, number|null>} code -> Prozent (null wenn Hauptgruppen-KV 0)
 */
export function computeMainGroupPercents(tree, totals) {
  const percents = {};

  const mainGroupOf = (node) => {
    let cur = node;
    let guard = 0;
    while (cur && cur.level > 1 && cur.parentCode && tree[cur.parentCode] && guard < 20) {
      cur = tree[cur.parentCode];
      guard += 1;
    }
    return cur;
  };

  Object.values(tree).forEach((node) => {
    const mg = mainGroupOf(node);
    const mgKv = mg && totals[mg.code] ? totals[mg.code].kv : 0;
    const ownKv = totals[node.code] ? totals[node.code].kv : 0;
    percents[node.code] = mgKv > 0 ? Math.round((ownKv / mgKv) * 1000) / 10 : null;
  });

  return percents;
}
